import { IconChevronDown } from '@tabler/icons-react';
import { InlineSelect } from '@heroui-pro/react';
import { Button, Checkbox, ListBox, Popover, Switch } from '@heroui/react';

import {
  AI_BILLING_INPUT_PARAMETER_LABELS,
  AI_BILLING_RESOLUTIONS,
  type BillingRuleInputParameters,
  isBillingRuleInputParametersConfigured,
} from '../constants';

interface BillingRuleInputParametersFieldProps {
  value?: BillingRuleInputParameters;
  onChange: (next: BillingRuleInputParameters | undefined) => void;
}

function cloneParams(value?: BillingRuleInputParameters): BillingRuleInputParameters {
  return value ? { ...value } : {};
}

function ParamEnableCheckbox({
  label,
  isSelected,
  onSelectedChange,
}: {
  label: string;
  isSelected: boolean;
  onSelectedChange: (selected: boolean) => void;
}) {
  return (
    <Checkbox
      aria-label={label}
      isSelected={isSelected}
      onChange={onSelectedChange}
    >
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
      </Checkbox.Content>
    </Checkbox>
  );
}

export function BillingRuleInputParametersField({
  value,
  onChange,
}: BillingRuleInputParametersFieldProps) {
  const params = value ?? {};
  const hasVideoInputEnabled = params.hasVideoInput !== undefined;
  const hasAudioOutputEnabled = params.hasAudioOutput !== undefined;
  const resolutionEnabled = params.resolution !== undefined;

  const commit = (next: BillingRuleInputParameters) => {
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };

  const setBooleanParam = (
    key: 'hasVideoInput' | 'hasAudioOutput',
    enabled: boolean,
    boolValue?: boolean
  ) => {
    const next = cloneParams(params);
    if (!enabled) {
      delete next[key];
      commit(next);
      return;
    }
    next[key] = boolValue ?? false;
    commit(next);
  };

  const setResolutionParam = (enabled: boolean, resolution?: (typeof AI_BILLING_RESOLUTIONS)[number]) => {
    const next = cloneParams(params);
    if (!enabled) {
      delete next.resolution;
      commit(next);
      return;
    }
    next.resolution = resolution ?? AI_BILLING_RESOLUTIONS[0];
    commit(next);
  };

  const triggerLabel = isBillingRuleInputParametersConfigured(params) ? '已配置' : '无配置';

  return (
    <Popover>
      <Popover.Trigger>
        <Button
          type="button"
          aria-label="参数规格"
          variant="secondary"
          className="h-9 w-full min-w-0 justify-between gap-1 px-3 font-normal"
        >
          <span className="truncate">{triggerLabel}</span>
          <IconChevronDown className="size-4 shrink-0 opacity-60" stroke={1.5} />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        className="w-[min(100vw-2rem,22rem)] p-3"
        placement="bottom start"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col divide-y divide-border">
          <div className="flex items-center gap-2 py-2.5 first:pt-0">
            <ParamEnableCheckbox
              label={`启用${AI_BILLING_INPUT_PARAMETER_LABELS.hasVideoInput}`}
              isSelected={hasVideoInputEnabled}
              onSelectedChange={(selected) => setBooleanParam('hasVideoInput', selected)}
            />
            <span className="min-w-0 flex-1 text-sm">{AI_BILLING_INPUT_PARAMETER_LABELS.hasVideoInput}</span>
            <span className="w-6 shrink-0 text-right text-sm text-muted-foreground">
              {hasVideoInputEnabled && params.hasVideoInput ? '有' : '无'}
            </span>
            <Switch
              aria-label={AI_BILLING_INPUT_PARAMETER_LABELS.hasVideoInput}
              isSelected={!!params.hasVideoInput}
              isDisabled={!hasVideoInputEnabled}
              onChange={(selected) => setBooleanParam('hasVideoInput', true, selected)}
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Content>
            </Switch>
          </div>

          <div className="flex items-center gap-2 py-2.5">
            <ParamEnableCheckbox
              label={`启用${AI_BILLING_INPUT_PARAMETER_LABELS.hasAudioOutput}`}
              isSelected={hasAudioOutputEnabled}
              onSelectedChange={(selected) => setBooleanParam('hasAudioOutput', selected)}
            />
            <span className="min-w-0 flex-1 text-sm">{AI_BILLING_INPUT_PARAMETER_LABELS.hasAudioOutput}</span>
            <span className="w-6 shrink-0 text-right text-sm text-muted-foreground">
              {hasAudioOutputEnabled && params.hasAudioOutput ? '有' : '无'}
            </span>
            <Switch
              aria-label={AI_BILLING_INPUT_PARAMETER_LABELS.hasAudioOutput}
              isSelected={!!params.hasAudioOutput}
              isDisabled={!hasAudioOutputEnabled}
              onChange={(selected) => setBooleanParam('hasAudioOutput', true, selected)}
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Content>
            </Switch>
          </div>

          <div className="flex items-center gap-2 py-2.5 last:pb-0">
            <ParamEnableCheckbox
              label={`启用${AI_BILLING_INPUT_PARAMETER_LABELS.resolution}`}
              isSelected={resolutionEnabled}
              onSelectedChange={(selected) => setResolutionParam(selected)}
            />
            <span className="min-w-0 flex-1 text-sm">{AI_BILLING_INPUT_PARAMETER_LABELS.resolution}</span>
            <InlineSelect
              aria-label={AI_BILLING_INPUT_PARAMETER_LABELS.resolution}
              className="min-w-[5.5rem] shrink-0"
              isDisabled={!resolutionEnabled}
              placeholder="请选择"
              value={params.resolution ?? null}
              onChange={(key) => {
                if (key == null) return;
                setResolutionParam(true, String(key) as (typeof AI_BILLING_RESOLUTIONS)[number]);
              }}
            >
              <InlineSelect.Trigger>
                <InlineSelect.Value />
                <InlineSelect.Indicator />
              </InlineSelect.Trigger>
              <InlineSelect.Popover className="min-w-[5.5rem]">
                <ListBox>
                  {AI_BILLING_RESOLUTIONS.map((r) => (
                    <ListBox.Item key={r} id={r} textValue={r}>
                      {r}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </InlineSelect.Popover>
            </InlineSelect>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}
